import {
  HeaderBack,
  HeaderBadge,
  HeaderLayout,
} from '@mentimeter/editor-header';
import { type TrackingContext } from '@mentimeter/http-clients';
import { Device, useDevice, useMatch } from '@mentimeter/ragnar-device';
import type { UserT } from '@mentimeter/user';
import { Box } from '@mentimeter/ragnar-ui/box';
import { TextEllipsis } from '@mentimeter/ragnar-ui/text-ellipsis';
import { useHeaderBackButton } from '../useHeaderBackButton';
import { CopyToAccountUser, CopyToAccountVisitor } from './CopyToAccount';
import { HeaderRightUser } from './HeaderRightUser';
import { ViewOnlyAuthButtons } from './ViewOnlyAuthButtons';

interface Props {
  trackingContext: TrackingContext;
  userIsCollaborator: boolean;
  user: UserT | null;
  seriesId: string;
  seriesName: string;
  seriesWorkspaceId: number | null;
  badgeLabel: string;
}

export const HeaderViewOnly = ({
  trackingContext,
  userIsCollaborator,
  user,
  seriesId,
  seriesName,
  seriesWorkspaceId,
  badgeLabel,
}: Props) => {
  const isAuthenticatedUser = Boolean(user);
  const { backHref, backLabel, onGoBack } = useHeaderBackButton(
    trackingContext,
    isAuthenticatedUser,
  );

  const { breakpoint } = useDevice();

  const buttonSize = breakpoint > 3 ? 'default' : 'compact';

  const isMobile = useMatch({ lessThan: 2 });

  const showCopyButtonOnHeaderLeft = !isMobile;
  const showCopyButtonOnHeaderRight = isMobile;

  const copyButton = isAuthenticatedUser ? (
    <CopyToAccountUser
      trackingContext={trackingContext}
      buttonSize={buttonSize}
      seriesId={seriesId}
    />
  ) : (
    <CopyToAccountVisitor
      seriesId={seriesId}
      buttonSize={buttonSize}
      trackingContext={trackingContext}
    />
  );

  return (
    <HeaderLayout>
      <HeaderLayout.Left>
        <HeaderBack
          backLinkHref={backHref}
          tooltipText={backLabel}
          onBackLinkClick={onGoBack}
          size={buttonSize}
          showBackButton={isAuthenticatedUser}
        >
          <Box
            maxWidth={breakpoint > 1 ? 320 : '80%'}
            flexDirection="row"
            alignItems="center"
            gap="space1"
          >
            <TextEllipsis
              id={`menti-name-${seriesId}`}
              fontSize="87.5"
              fontWeight="semiBold"
              text={seriesName}
              tooltipDescription={seriesName}
            />
          </Box>

          {showCopyButtonOnHeaderLeft && copyButton}
          <Device.Match greaterThan={1}>
            <HeaderBadge
              text={`${badgeLabel} only`}
              bg="brandWeakest"
              ml="space2"
              textTransform="none"
            />
          </Device.Match>
        </HeaderBack>
      </HeaderLayout.Left>

      <HeaderLayout.Right>
        {isAuthenticatedUser && user ? (
          <HeaderRightUser
            buttonSize={buttonSize}
            seriesId={seriesId}
            trackingContext={trackingContext}
            userIsCollaborator={userIsCollaborator}
            user={user}
            copyButton={showCopyButtonOnHeaderRight ? copyButton : null}
          />
        ) : showCopyButtonOnHeaderRight ? (
          copyButton
        ) : (
          <ViewOnlyAuthButtons
            buttonSize={buttonSize}
            seriesId={seriesId}
            trackingContext={trackingContext}
          />
        )}
      </HeaderLayout.Right>
    </HeaderLayout>
  );
};
